import { memo } from 'react';
import { LayoutDashboard, Filter, Lightbulb, Video, Scissors } from 'lucide-react';

const NAVIGATION = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'bg-blue-100 text-blue-600' },
  { id: 'pipeline', label: 'Pipeline', icon: Filter, color: 'bg-violet-100 text-violet-600' },
  { id: 'ideas', label: 'Ideas', icon: Lightbulb, color: 'bg-amber-100 text-amber-600' },
  { id: 'recordings', label: 'Recordings', icon: Video, color: 'bg-purple-100 text-purple-600' },
];

export const Sidebar = memo(({ view, setView, stats, user, mobile }) => {
  if (mobile) {
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex md:hidden z-40">
        {NAVIGATION.map(nav => {
          const badge = nav.id === 'ideas' && user === 'zein' ? stats.pending : null;
          return (
            <button
              key={nav.id}
              onClick={() => setView(nav.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition ${
                view === nav.id ? 'text-violet-600' : 'text-slate-400'
              }`}
            >
              <div className="relative">
                <nav.icon className="w-5 h-5" />
                {badge > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 bg-amber-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {badge}
                  </span>
                )}
              </div>
              <span>{nav.label}</span>
            </button>
          );
        })}
      </nav>
    );
  }

  return (
    <aside className="w-64 bg-white border-r border-slate-200 flex-shrink-0 hidden md:flex flex-col">
      <nav className="p-4 space-y-2 flex-1">
        {NAVIGATION.map(nav => {
          const badge = nav.id === 'ideas' && user === 'zein' ? stats.pending : null;

          return (
            <button
              key={nav.id}
              onClick={() => setView(nav.id)}
              className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-medium transition ${
                view === nav.id
                  ? 'bg-slate-100 text-slate-900'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                view === nav.id ? nav.color : 'bg-slate-100 text-slate-500'
              }`}>
                <nav.icon className="w-5 h-5" />
              </div>
              <span>{nav.label}</span>
              {badge > 0 && (
                <span className="ml-auto bg-amber-500 text-white text-xs px-2.5 py-1 rounded-full font-bold">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Quick Stats */}
      <div className="p-4 border-t border-slate-200 space-y-3">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">Quick Stats</p>
        <div className="space-y-2">
          <StatItem label="Ideas" value={stats.ideas} />
          <StatItem label="Active" value={stats.active} />
          <StatItem label="Cutdowns" value={stats.cutdowns} variant="orange" icon={<Scissors className="w-4 h-4" />} />
          {stats.overdue > 0 && <StatItem label="Overdue" value={stats.overdue} variant="red" />}
        </div>
      </div>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

const StatItem = memo(({ label, value, variant = 'default', icon }) => {
  const bgColor = variant === 'orange' ? 'bg-orange-50' : variant === 'red' ? 'bg-red-50' : 'bg-slate-50';
  const textColor = variant === 'orange' ? 'text-orange-600' : variant === 'red' ? 'text-red-600' : 'text-slate-600';
  const valueColor = variant === 'orange' ? 'text-orange-600' : variant === 'red' ? 'text-red-600' : 'text-slate-800';

  return (
    <div className={`flex justify-between items-center px-4 py-2.5 ${bgColor} rounded-xl`}>
      <span className={`${textColor} font-medium flex items-center gap-1.5`}>
        {icon}
        {label}
      </span>
      <span className={`font-bold ${valueColor} text-lg`}>{value}</span>
    </div>
  );
});

StatItem.displayName = 'StatItem';
